import React, { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Context from './Context';
import WidgetHeader from './WidgetHeader/WidgetHeader';
import { Namespace } from '../../../translation/namespace';
import { isHidden } from '../../../lib/BlockUtils/BlockUtils';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { ISonURL } from '../../../types/IUrl';
import { SVGIcon } from '../../atoms/SVG';
import { BlockType, ElementType, WidgetType } from '../../../types/api/ElementType';
import { WidgetWrapperElement } from './WidgetWrapperElement';
import { HeadingHierarchy } from '../../../_lib/components/Heading/Heading';

type Block = {
  uuid: string;
  // can't be required because dummy widgets doesn't have a self url
  self?: ISonURL;
  elementType: string;
  embedded: any;
};

export type WidgetTypeDefinition = {
  uuid: string;
  elementType: ElementType | BlockType | WidgetType;
  blocks: Block[];
  header: boolean;
  title: string;
  iconId: SVGIcon;
  footer?: { actions: { [key: string]: string } };
  invisible?: boolean;
};

type WidgetProps = {
  isArticle: boolean;
  frameless?: boolean;
  blockComponents: ReactNode;
  nextIsFrameless?: boolean;
  widget: WidgetTypeDefinition;
  headlineHierarchy?: HeadingHierarchy;
};

export default function Widget({
  widget,
  blockComponents,
  nextIsFrameless = false,
  frameless = false,
  isArticle,
  headlineHierarchy,
}: WidgetProps) {
  const { t } = useTranslation(Namespace.Widget);

  const blocks = widget.blocks.filter(block => !isHidden(block));
  // widget.title and widget.icon property directly on widget, can be configured via compositon.json
  // widget.blocks[0]?.embedded?.widgetTitle for compatibility with GenericSearchList
  const titleOverwrite = widget.title || blocks[0]?.embedded?.widgetTitle;
  const iconOverwrite = widget.iconId || null;

  const classes = ['widget'];

  if (nextIsFrameless) {
    classes.push('widget--no-margin-bottom');
  }
  if (frameless) {
    classes.push('widget--frameless');
  }

  classes.push('widget__content');

  if (isArticle) {
    classes.push('widget__content--article');
  }

  const widgetId = `widget-frame-${widget.elementType}-${widget.uuid}`;
  const contextValue = useMemo(
    () =>
      ({
        titleOverwrittenBy: titleOverwrite && blocks[0].elementType,
        widgetElementType: widget.elementType,
        widgetUuid: widget.uuid,
        widgetId,
        header: widget.header,
      }) as any,
    [titleOverwrite, widget.elementType, widget.uuid, widget.header, widgetId, blocks]
  );

  return (
    <WidgetWrapperElement
      className={classes.join(' ')}
      id={widgetId}
      ariaLabelledby={isArticle ? GlobalDocumentIds.pageTitle : `widget-header-${widget.elementType}-${widget.uuid}`}
      isArticle={isArticle}
    >
      <div className="widget__rounded-corners-top"></div>
      <Context.Provider value={contextValue}>
        {widget.header && (
          <WidgetHeader
            svgSpriteId={iconOverwrite || widget.elementType}
            title={titleOverwrite || t(`header.${widget.elementType}`)}
            headlineHierarchy={headlineHierarchy}
            hasIcon={frameless === false}
            invisible={frameless === true}
            id={`${widget.elementType}-${widget.uuid}`}
          />
        )}
        {blockComponents}
      </Context.Provider>
      <div className="widget__rounded-corners-bottom"></div>
    </WidgetWrapperElement>
  );
}
